import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import SchoolIcon from '@material-ui/icons/School';
import PersonIcon from '@material-ui/icons/Person';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import LockIcon from '@material-ui/icons/Lock';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import Loading from '../../components/Loading';
import { remoteRoutes } from '../../data/constants';
import { IState } from '../../data/types';
import { get, post } from '../../utils/ajax';
import Toast from '../../utils/Toast';
import { fontFamilies } from '../../theme/fonts';

import { colors } from '../../theme/tokens';
const CORAL = colors.progressFill;
const ORANGE = colors.accentOrange;
const DARK = colors.textPrimary;

const useStyles = makeStyles((theme: Theme) => ({
  root: { padding: theme.spacing(3), minHeight: '100%' },
  pageSubtitle: {
    fontSize: 15,
    color: colors.textMuted,
    marginTop: 2,
    marginBottom: theme.spacing(2.5),
  },
  searchBox: {
    background: colors.bgSurface,
    borderRadius: 10,
    marginBottom: theme.spacing(2.5),
    '& .MuiOutlinedInput-root': { borderRadius: 10, fontSize: 13 },
  },
  card: {
    background: colors.bgSurface,
    borderRadius: 14,
    border: `1px solid ${colors.border}`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as any,
    height: '100%',
    transition: 'box-shadow 0.15s',
    '&:hover': { boxShadow: `0 6px 24px ${colors.shadow}` },
  },
  cardTop: { padding: '18px 20px 16px', flex: 1 },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  enrollBadge: {
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 20,
    padding: '3px 10px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  enrollOpen: {
    background: colors.tintGreen,
    color: colors.accentGreen,
    border: `1px solid ${colors.tintGreen}`,
  },
  enrollClosed: {
    background: colors.bgRaised,
    color: colors.textMuted,
    border: `1px solid ${colors.bgRaised}`,
  },
  courseName: {
    fontSize: 16,
    fontWeight: 700,
    color: DARK,
    lineHeight: 1.3,
    marginBottom: 8,
  },
  courseDesc: {
    fontSize: 13,
    color: colors.textMuted,
    lineHeight: 1.55,
    marginBottom: 12,
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  } as any,
  instructorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },
  instructorAvatar: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${CORAL}, ${ORANGE})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: colors.onStat,
    flexShrink: 0,
  },
  cardFooter: {
    borderTop: `1px solid ${colors.border}`,
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: colors.bgSubtle,
  },
  enrollBtn: {
    background: `linear-gradient(90deg, ${CORAL} 0%, ${ORANGE} 100%)`,
    color: colors.onStat,
    borderRadius: 8,
    fontWeight: 700,
    textTransform: 'none' as any,
    boxShadow: 'none',
    fontSize: 12,
    '&:hover': { opacity: 0.9 },
    '&:disabled': { opacity: 0.55, color: colors.onStat },
  },
  emptyBox: {
    background: colors.bgSurface,
    borderRadius: 16,
    border: `1px solid ${colors.border}`,
    padding: '60px 24px',
    textAlign: 'center' as any,
  },
}));

function initials(name: string) {
  return (name || '')
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

const GENDERS = ['Male', 'Female'];

const CourseCatalog = () => {
  const classes = useStyles();
  const user = useSelector((state: IState) => state.core.user);
  const [courses, setCourses] = useState<any[]>([]);
  const [enrolledIds, setEnrolledIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  // Whether this account has enrolled in anything before, ever (any status).
  // The trimmed signup never collects a hub/gender/DOB/phone, so the first
  // enrollment is also where a student record (and those details) finally
  // get created — everyone after that just enrolls with one click.
  const [hasStudentRecord, setHasStudentRecord] = useState(true);
  const [hubOptions, setHubOptions] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [detailsCourseId, setDetailsCourseId] = useState<number | null>(null);
  const [detailsForm, setDetailsForm] = useState({
    hubId: '',
    gender: '',
    dateOfBirth: '',
    phone: '',
  });

  useEffect(() => {
    let done = 0;
    const finish = () => {
      done += 1;
      if (done === 2) setLoading(false);
    };

    get(
      remoteRoutes.courses,
      (data) => {
        setCourses(Array.isArray(data) ? data : []);
      },
      undefined,
      finish,
    );

    // The one screen that needs to see a request already in flight, so a
    // student isn't invited to ask for the same course twice. Everywhere
    // else a pending enrolment is deliberately invisible.
    get(
      `${remoteRoutes.myCourses}?includePending=true`,
      (data) => {
        const list: any[] = Array.isArray(data) ? data : [];
        setHasStudentRecord(list.length > 0);
        const enrolled = new Set<number>(
          list
            .filter((e) => e.status !== 'Pending')
            .map((e) => e.courseId || e.course?.id)
            .filter(Boolean),
        );
        const pending = new Set<number>(
          list
            .filter((e) => e.status === 'Pending')
            .map((e) => e.courseId || e.course?.id)
            .filter(Boolean),
        );
        setEnrolledIds(enrolled);
        setPendingIds(pending);
      },
      undefined,
      finish,
    );

    get(
      remoteRoutes.hubsCombo,
      (data) => setHubOptions(Array.isArray(data) ? data : []),
      undefined,
      undefined,
    );
  }, [user.contactId]);

  const submitEnrollment = (courseId: number, extra?: Record<string, any>) => {
    setEnrolling(courseId);
    post(
      remoteRoutes.enrollMe,
      { courseId, ...extra },
      (data: any) => {
        if (data?.status === 'Pending') {
          Toast.info('Enrollment request sent — awaiting approval.');
          setPendingIds((prev) => new Set(prev).add(courseId));
        } else {
          Toast.success('Enrolled successfully!');
          setEnrolledIds((prev) => new Set(prev).add(courseId));
        }
        setHasStudentRecord(true);
        setEnrolling(null);
        setDetailsCourseId(null);
      },
      (err: any) => {
        Toast.error(
          err?.response?.body?.message ||
            'Enrollment failed. Please try again.',
        );
        setEnrolling(null);
      },
    );
  };

  const handleEnroll = (courseId: number) => {
    if (!hasStudentRecord) {
      // First-ever enrollment — collect what the trimmed signup skipped.
      setDetailsForm({ hubId: '', gender: '', dateOfBirth: '', phone: '' });
      setDetailsCourseId(courseId);
      return;
    }
    submitEnrollment(courseId);
  };

  const handleDetailsSubmit = () => {
    if (!detailsCourseId) return;
    if (!detailsForm.hubId || !detailsForm.gender || !detailsForm.phone) {
      Toast.error('Please fill in every field');
      return;
    }
    submitEnrollment(detailsCourseId, {
      hubId: Number(detailsForm.hubId),
      gender: detailsForm.gender,
      dateOfBirth: detailsForm.dateOfBirth || undefined,
      phone: detailsForm.phone,
    });
  };

  // Only courses a student can actually join belong here. The catalogue
  // exists to enrol, so listing a closed course just offers something that
  // cannot be taken up — and the server refuses that enrolment anyway. A
  // course they are already in, or have a request pending on, stays
  // visible even once it closes, so an in-flight request does not vanish.
  const visible = courses.filter(
    (c) =>
      (c.isActive !== false && c.isEnrollable) ||
      enrolledIds.has(c.id) ||
      pendingIds.has(c.id),
  );

  const filtered = visible.filter((c) => {
    const name = (c.title || c.name || '').toLowerCase();
    const desc = (c.description || '').toLowerCase();
    const instr = (c.instructor || '').toLowerCase();
    const q = query.toLowerCase();
    return name.includes(q) || desc.includes(q) || instr.includes(q);
  });

  if (loading) {
    return (
      <Layout title="Course Catalog">
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout
      title="Course Catalog"
      subtitle="Browse available courses and enroll to start learning"
    >
      <div className={classes.root}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          className={classes.searchBox}
          placeholder="Search by course name, description or instructor…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 18, color: colors.textMuted }} />
              </InputAdornment>
            ),
          }}
        />

        {filtered.length === 0 ? (
          <div className={classes.emptyBox}>
            <SchoolIcon style={{ fontSize: 56, color: colors.textDisabled }} />
            <Typography
              style={{
                color: colors.textMuted,
                marginTop: 12,
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              {query
                ? 'No courses match your search'
                : 'No courses available yet'}
            </Typography>
            {!query && (
              <Typography
                variant="body2"
                style={{ color: colors.textMuted, marginTop: 6 }}
              >
                Check back soon — courses appear here while they are open for
                enrolment.
              </Typography>
            )}
          </div>
        ) : (
          <Grid container spacing={2}>
            {filtered.map((course: any) => {
              const isEnrolled = enrolledIds.has(course.id);
              const isPending = pendingIds.has(course.id);
              const isEnrolling = enrolling === course.id;
              const title = course.title || course.name || 'Untitled Course';
              const description = course.description || '';
              const instructor = course.instructor || '';

              return (
                <Grid item xs={12} sm={6} md={4} key={course.id}>
                  <div className={classes.card}>
                    <div className={classes.cardTop}>
                      {/* Open / Closed badge */}
                      <div className={classes.statusRow}>
                        <span
                          className={`${classes.enrollBadge} ${
                            course.isEnrollable
                              ? classes.enrollOpen
                              : classes.enrollClosed
                          }`}
                        >
                          {course.isEnrollable ? (
                            <>
                              <LockOpenIcon style={{ fontSize: 11 }} /> Open
                            </>
                          ) : (
                            <>
                              <LockIcon style={{ fontSize: 11 }} /> Closed
                            </>
                          )}
                        </span>
                      </div>

                      {/* Title */}
                      <div className={classes.courseName}>{title}</div>

                      {/* Description */}
                      <div
                        className={classes.courseDesc}
                        style={
                          !description
                            ? {
                                color: colors.textFaint,
                                fontStyle: 'italic',
                              }
                            : {}
                        }
                      >
                        {description || 'No description available.'}
                      </div>

                      {/* Instructor */}
                      <div className={classes.instructorRow}>
                        {instructor ? (
                          <>
                            <div className={classes.instructorAvatar}>
                              {initials(instructor)}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: colors.textStrong,
                                }}
                              >
                                {instructor}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: colors.textMuted,
                                }}
                              >
                                Instructor
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <PersonIcon
                              style={{
                                fontSize: 16,
                                color: colors.textDisabled,
                              }}
                            />
                            <span
                              style={{
                                fontSize: 12,
                                color: colors.textFaint,
                                fontStyle: 'italic',
                              }}
                            >
                              No instructor assigned
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className={classes.cardFooter}>
                      {isEnrolled ? (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: colors.accentGreen,
                          }}
                        >
                          ✓ Enrolled
                        </span>
                      ) : isPending ? (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: colors.accentAmber,
                          }}
                        >
                          ⏳ Pending approval
                        </span>
                      ) : course.isEnrollable ? (
                        <Button
                          size="small"
                          className={classes.enrollBtn}
                          disabled={isEnrolling}
                          onClick={() => handleEnroll(course.id)}
                          startIcon={
                            isEnrolling ? (
                              <CircularProgress
                                size={12}
                                style={{ color: colors.onStat }}
                              />
                            ) : undefined
                          }
                        >
                          {isEnrolling ? 'Enrolling…' : 'Enroll Now'}
                        </Button>
                      ) : (
                        <span style={{ fontSize: 12, color: colors.textMuted }}>
                          Enrollment closed
                        </span>
                      )}
                    </div>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        )}
      </div>

      {/* First-ever enrollment: the trimmed signup skipped these, so ask now. */}
      <Dialog
        open={detailsCourseId !== null}
        onClose={() => setDetailsCourseId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Just a few more details</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            style={{ color: colors.textMuted, marginBottom: 16 }}
          >
            We need these to set up your student record before enrolling you.
          </Typography>
          <TextField
            select
            fullWidth
            label="Learning Hub"
            variant="outlined"
            size="small"
            margin="dense"
            value={detailsForm.hubId}
            onChange={(e) => {
              const value = e.target.value;
              setDetailsForm((f) => ({ ...f, hubId: value }));
            }}
          >
            {hubOptions.map((h) => (
              <MenuItem key={h.id} value={h.id}>
                {h.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Gender"
            variant="outlined"
            size="small"
            margin="dense"
            value={detailsForm.gender}
            onChange={(e) => {
              const value = e.target.value;
              setDetailsForm((f) => ({ ...f, gender: value }));
            }}
          >
            {GENDERS.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            type="date"
            label="Date of Birth"
            variant="outlined"
            size="small"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={detailsForm.dateOfBirth}
            onChange={(e) => {
              const value = e.target.value;
              setDetailsForm((f) => ({ ...f, dateOfBirth: value }));
            }}
          />
          <TextField
            fullWidth
            label="Phone number"
            variant="outlined"
            size="small"
            margin="dense"
            placeholder="+256772123456"
            value={detailsForm.phone}
            onChange={(e) => {
              const value = e.target.value;
              setDetailsForm((f) => ({ ...f, phone: value }));
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsCourseId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={enrolling !== null}
            onClick={handleDetailsSubmit}
          >
            {enrolling !== null ? 'Enrolling…' : 'Enroll'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default CourseCatalog;
